import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useProposalContext } from "./ProposalContext";
import type { ResearchDepth, ResearchProposal } from "./researchModel";

export type Decision = "run" | "decline";

/** A proposal card's choice: the depth, then Start research or Just answer now, sent once (a double click too). */
export function useProposalDecision(proposal: ResearchProposal) {
  const { t } = useTranslation("chat", { keyPrefix: "research.proposal" });
  const { onDecide } = useProposalContext();
  const [depth, setDepth] = useState<ResearchDepth>(proposal.depth);
  const [deciding, setDeciding] = useState<Decision | null>(null);
  // Set before the send starts, so a second click while it's on its way decides nothing.
  const sent = useRef(false);

  const decide = (decision: Decision) => {
    if (sent.current || !onDecide) return;
    sent.current = true;
    setDeciding(decision);
    // Only a depth I changed: otherwise the run uses the proposal as stored.
    const overrides = decision === "run" && depth !== proposal.depth ? { depth } : undefined;
    onDecide(
      t(decision === "run" ? "start" : "decline"),
      { id: proposal.id, decision, ...(overrides && { overrides }) },
      () => {
        sent.current = false;
        setDeciding(null);
      },
    );
  };

  return { depth, setDepth, deciding, decide };
}
